import * as React from "react";
import { createRoot } from "react-dom/client";
import { Image, Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";

import { CredentialForm } from "../components/CredentialForm";
import { ThemedRoot } from "../components/ThemedRoot";
import { sendSessionToParent } from "../lib/officeDialog";

import wordmark from "../../assets/valoon-wordmark.png";

/* global document, Office, HTMLElement */

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "20px", padding: "20px 20px 28px" },
  brand: { display: "flex", justifyContent: "center" },
  lede: { color: tokens.colorNeutralForeground2, textAlign: "center" },
  centered: { display: "flex", justifyContent: "center", padding: "40px 0" },
});

/**
 * The dialog's contents.
 *
 * Unlike the task pane this genuinely must wait for Office: its only way to
 * return a result is Office.context.ui.messageParent, so rendering a form the
 * user could submit before Office existed would strand them.
 */
const Dialog: React.FC = () => {
  const styles = useStyles();
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    // A dialog is its own execution context, with its own Office.onReady.
    Office.onReady(() => setReady(true));
  }, []);

  if (!ready) {
    return (
      <div className={styles.centered}>
        <Spinner label="Starting…" />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.brand}>
        <Image src={wordmark} alt="Valoon" height={40} fit="contain" />
      </div>
      <Text className={styles.lede}>Sign in with your Valoon API credentials.</Text>
      {/* The session goes back to the pane over messageParent. Same origin, so
          no targetOrigin — and never "*", because this message is a token. */}
      <CredentialForm onSignedIn={sendSessionToParent} />
    </div>
  );
};

const rootElement: HTMLElement | null = document.getElementById("container");
createRoot(rootElement!).render(
  <ThemedRoot>
    <Dialog />
  </ThemedRoot>
);
